import { useNavigate } from "react-router-dom";
import "../App.css";

export default function HelpDocumentationPage() {
  const navigate = useNavigate();

  return (
    <div className="page-container">
      <div className="page-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>Help & Documentation</h1>
      </div>

      <div className="settings-container">
        {/* Getting Started */}
        <div className="settings-section">
          <h3 className="settings-section-title">Getting Started</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">rocket_launch</span>
            </div>
            <div className="help-content">
              <h4>Quick Start Guide</h4>
              <p>
                Welcome to PhysioKea! Start by adding your first patient from
                the Patients page. Then schedule appointments and track their
                progress with our intuitive dashboard.
              </p>
              <ul className="help-list">
                <li>Navigate to Patients and click "Add Patient"</li>
                <li>Fill in patient details and medical history</li>
                <li>Create appointments from the Appointments page</li>
                <li>Track progress and generate reports</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Patient Management */}
        <div className="settings-section">
          <h3 className="settings-section-title">Patient Management</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">person</span>
            </div>
            <div className="help-content">
              <h4>Managing Patients</h4>
              <p>
                Efficiently manage your patient records with comprehensive
                profiles and medical histories.
              </p>
              <ul className="help-list">
                <li>
                  <strong>Add Patient:</strong> Click the "+" button on the
                  Patients page
                </li>
                <li>
                  <strong>Edit Patient:</strong> Click on any patient card to
                  view details, then click edit
                </li>
                <li>
                  <strong>Search:</strong> Use the search bar to quickly find
                  patients by name or phone
                </li>
                <li>
                  <strong>Filter:</strong> Filter patients by status (Active,
                  Inactive, etc.)
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Appointments */}
        <div className="settings-section">
          <h3 className="settings-section-title">Appointments</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">calendar_today</span>
            </div>
            <div className="help-content">
              <h4>Scheduling & Management</h4>
              <p>
                Keep track of all your appointments with easy scheduling and
                status management.
              </p>
              <ul className="help-list">
                <li>
                  <strong>Create Appointment:</strong> Click "+" on Appointments
                  page, select patient, date, and time
                </li>
                <li>
                  <strong>Update Status:</strong> Mark appointments as
                  Completed, Cancelled, or No-show
                </li>
                <li>
                  <strong>View Details:</strong> Click any appointment to see
                  full details and patient info
                </li>
                <li>
                  <strong>Notifications:</strong> Enable notifications to get
                  reminders before appointments
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Billing & Payments */}
        <div className="settings-section">
          <h3 className="settings-section-title">Billing & Payments</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">payments</span>
            </div>
            <div className="help-content">
              <h4>Managing Finances</h4>
              <p>Track payments, generate invoices, and manage billing.</p>
              <ul className="help-list">
                <li>
                  <strong>Record Payment:</strong> In appointment details, click
                  "Add Payment"
                </li>
                <li>
                  <strong>Payment Plans:</strong> Set up installment plans for
                  patients
                </li>
                <li>
                  <strong>Reports:</strong> View financial reports and analytics
                  in the Reports section
                </li>
                <li>
                  <strong>History:</strong> Track all payment history for each
                  patient
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="settings-section">
          <h3 className="settings-section-title">Notifications</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">notifications</span>
            </div>
            <div className="help-content">
              <h4>Stay Updated</h4>
              <p>
                Configure notification preferences to stay informed about
                appointments and important events.
              </p>
              <ul className="help-list">
                <li>
                  <strong>Enable:</strong> Go to Settings → Notification
                  Settings
                </li>
                <li>
                  <strong>Appointment Reminders:</strong> Get notified before
                  appointments start
                </li>
                <li>
                  <strong>Payment Alerts:</strong> Receive notifications for
                  pending payments
                </li>
                <li>
                  <strong>System Updates:</strong> Stay informed about new
                  features and updates
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Security */}
        <div className="settings-section">
          <h3 className="settings-section-title">Data Security</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">shield</span>
            </div>
            <div className="help-content">
              <h4>Your Data is Protected</h4>
              <p>
                PhysioKea uses industry-standard encryption and security
                measures to protect your data.
              </p>
              <ul className="help-list">
                <li>All data is encrypted at rest and in transit</li>
                <li>Regular security audits and updates</li>
                <li>HIPAA-compliant data handling</li>
                <li>Two-factor authentication available</li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="settings-section">
          <h3 className="settings-section-title">Frequently Asked Questions</h3>
          
          <div className="faq-item">
            <h4>
              <span className="material-symbols-outlined">help</span>
              How do I reset my password?
            </h4>
            <p>
              Go to Settings → Privacy & Security → Change Password. You'll need
              to enter your current password and then set a new one.
            </p>
          </div>

          <div className="faq-item">
            <h4>
              <span className="material-symbols-outlined">help</span>
              Can I export my patient data?
            </h4>
            <p>
              Yes! Go to the Reports page and select "Export Data". You can
              export patient lists, appointment history, and financial reports.
            </p>
          </div>

          <div className="faq-item">
            <h4>
              <span className="material-symbols-outlined">help</span>
              How do I cancel an appointment?
            </h4>
            <p>
              Click on the appointment in your Appointments page, then click the
              "Cancel" button. You can optionally add a cancellation reason.
            </p>
          </div>

          <div className="faq-item">
            <h4>
              <span className="material-symbols-outlined">help</span>
              Is my data backed up?
            </h4>
            <p>
              Yes, all your data is automatically backed up to secure cloud
              servers with redundancy. Your data is safe and can be recovered if
              needed.
            </p>
          </div>
        </div>

        {/* Contact Support */}
        <div className="settings-section">
          <h3 className="settings-section-title">Need More Help?</h3>
          <div className="help-card">
            <div className="help-icon">
              <span className="material-symbols-outlined">support_agent</span>
            </div>
            <div className="help-content">
              <h4>Contact Support</h4>
              <p>
                Our support team is here to help you with any questions or
                issues.
              </p>
              <div className="help-actions">
                <button
                  onClick={() => navigate("/contact")}
                  className="btn btn-primary"
                >
                  <span className="material-symbols-outlined">email</span>
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
